import { api, type StreamProfile } from './api';
import { clientId } from './clientId';

/** §D3.4 aturan 2 — klien menyapa tiap 20 detik. */
const INTERVAL_MS = 20_000;

let pemakai = 0;
let timer: number | undefined;

/**
 * Aliran yang **sedang benar-benar ditampilkan** tab ini, `{kamera}:{profil}`
 * → berapa elemen yang memakainya (§D3.4 aturan 3).
 *
 * Penghitung, bukan himpunan: satu kamera bisa tampil dua kali sekaligus —
 * substream di belakang gambar HD di layar penuh adalah kasus normalnya — dan
 * yang berhenti duluan tidak boleh mencoret kamera yang masih dipakai yang lain.
 */
const aktif = new Map<string, number>();

/**
 * Nyatakan satu aliran sedang tampil; kembaliannya melepasnya lagi.
 *
 * Inilah yang membuat sapaan berhenti sekadar berkata "tab saya masih hidup"
 * dan mulai berkata "dan ini yang masih saya tampilkan". Tanpa itu, aliran
 * kamera yang sudah ditinggalkan — pindah halaman, keluar dari layar penuh —
 * tidak punya siapa pun yang bisa memutusnya: browser tidak selalu menutup
 * koneksi `multipart/x-mixed-replace` yang elemennya dicabut, dan Zoraxy tidak
 * meneruskan kabar putus ke hulu.
 */
export function pakaiAliran(cameraId: string, profile: StreamProfile): () => void {
  const kunci = `${cameraId}:${profile}`;
  aktif.set(kunci, (aktif.get(kunci) ?? 0) + 1);
  jadwalkanSapa();
  return () => {
    const sisa = (aktif.get(kunci) ?? 1) - 1;
    if (sisa > 0) aktif.set(kunci, sisa);
    else aktif.delete(kunci);
    // Melepas jauh lebih mendesak daripada memakai: aliran HD yang ditinggalkan
    // mendorong belasan Mbps, dan menunggu tick 20 detik berikutnya berarti
    // membayarnya belasan Mbps x 20 detik setiap kali orang menekan Esc.
    jadwalkanSapa();
  };
}

/**
 * Sapaan menyusul perubahan daftar, disatukan dalam satu tick.
 *
 * Satu pergantian halaman 5x5 melepas 25 aliran dan memakai 25 yang baru dalam
 * satu commit React; tanpa penggabungan ini itu berarti 50 permintaan.
 */
let tundaan: number | undefined;
function jadwalkanSapa(): void {
  if (tundaan !== undefined) return;
  tundaan = window.setTimeout(() => {
    tundaan = undefined;
    void sapa();
  }, 250);
}

async function sapa(): Promise<void> {
  try {
    await api('/stream/heartbeat', {
      method: 'POST',
      body: JSON.stringify({ clientId: clientId(), streams: [...aktif.keys()] }),
    });
  } catch {
    // Sengaja diam. Sapaan yang gagal berarti tile terputus paling lama semenit
    // lagi, dan tile memang sudah tahu cara menyambung sendiri (§G8.1).
    // Menaikkan error ke UI di sini hanya menakuti orang tanpa memberi tindakan.
  }
}

/**
 * Nyalakan sapaan selama halaman ini punya tile yang hidup.
 *
 * Dihitung dengan penghitung pemakai, bukan boolean: dashboard dan viewer
 * fullscreen bisa aktif bersamaan, dan yang berhenti duluan tidak boleh
 * mematikan sapaan milik yang lain.
 */
export function mulaiHeartbeat(): () => void {
  pemakai += 1;
  if (pemakai === 1) {
    void sapa();
    timer = window.setInterval(() => void sapa(), INTERVAL_MS);
  }
  return () => {
    pemakai -= 1;
    if (pemakai === 0) {
      window.clearInterval(timer);
      timer = undefined;
      // Satu sapaan terakhir dengan daftar yang sudah kosong. Tanpa ini,
      // meninggalkan dashboard ke halaman yang tidak punya tile menyisakan
      // seluruh aliran tab ini mengalir sampai penyapu §D3.4 memutuskan tab-nya
      // sudah pergi — 60 detik penuh gambar yang tidak dilihat siapa pun.
      jadwalkanSapa();
    }
  };
}

/**
 * Ref untuk `<img>` MJPEG yang **membatalkan** alirannya begitu elemennya lepas
 * dari DOM (§D3.4).
 *
 * Alasannya sama dengan yang sudah ada di `CameraTile`: melepas `<img>` dari
 * DOM tidak menjamin browser memutus koneksi `multipart/x-mixed-replace` yang
 * memang tidak pernah selesai, dan Zoraxy tidak meneruskan kabar putus ke hulu
 * — jadi aliran yang ditinggalkan terus mengalir. Bedanya, ref ini dipanggil
 * React dengan `null` **sebelum** elemennya dicabut, jadi `src` diganti selagi
 * elemennya masih menempel: itu titik pembatalan yang paling bisa diandalkan.
 *
 * Identitasnya harus tetap antar-render — simpan lewat `useRef(...).current`.
 * Ref yang berganti tiap render akan dipanggil ulang dengan `null` dan
 * membatalkan aliran yang justru sedang hidup.
 */
export function refAliranMjpeg(): (el: HTMLImageElement | null) => void {
  let terpasang: HTMLImageElement | null = null;
  return (el) => {
    if (el) {
      terpasang = el;
      return;
    }
    const lama = terpasang;
    terpasang = null;
    // Mengganti `src`, bukan `removeAttribute`: yang membatalkan permintaan
    // adalah permintaan baru yang menggantikannya.
    if (lama) lama.src = '';
  };
}
